import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

export const MaskedLines = ({ lines, className = "", delay = 0, as: Tag = "h1" }) => (
  <Tag className={className}>
    {lines.map((line, i) => (
      <span key={i} className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
        <motion.span
          className="block will-change-transform"
          initial={{ y: "115%" }}
          animate={{ y: "0%" }}
          transition={{ duration: 1.15, delay: delay + i * 0.14, ease: EASE }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </Tag>
);

export const Reveal = ({ children, delay = 0, y = 28, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.9, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

export const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 1, delay, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

export const ParallaxImage = ({ src, alt, className = "", speed = 0.12, testId, position = "50% 50%" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [`-${speed * 100}%`, `${speed * 100}%`]);
  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        data-testid={testId}
        style={{ y, scale: 1 + speed * 2, objectPosition: position }}
        className="h-full w-full object-cover will-change-transform"
      />
    </div>
  );
};

export const Marquee = ({ items }) => {
  const row = [...items, ...items];
  return (
    <div className="overflow-hidden border-y border-gold/25 bg-navy-900 py-5" data-testid="editorial-marquee">
      <div className="marquee-track">
        {[0, 1].map((half) => (
          <div key={half} className="flex shrink-0 items-center">
            {row.map((item, i) => (
              <span key={`${half}-${i}`} className="flex items-center whitespace-nowrap">
                <span className="font-serif text-lg italic text-cream/85 sm:text-xl">{item}</span>
                <span className="mx-8 text-gold text-xs">✦</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export const Kicker = ({ children, light = false }) => (
  <p className={`flex items-center gap-4 text-sm uppercase tracking-[0.3em] font-medium ${light ? "text-gold" : "text-gold-deep"}`}>
    <span className={`inline-block h-px w-16 ${light ? "bg-gold" : "bg-gold/70"}`} />
    {children}
  </p>
);
